import React, { useState, useMemo } from "react";
import { Modal, Input, Button, Checkbox, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { axiosDoctorAuth, axiosAuth, axiosUsers } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import PrivacyPolicyPopup from "../../../navigation/Navbar/PrivacyPolicyPopup/PrivacyPolicyPopup";
import "./switchModalOpen.css";

/**
 * Décode le payload d'un JWT sans dépendance externe.
 */
const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Recherche générique d'un champ "email" dans le state Redux global.
 */
const findEmailInState = (fullState, priorityKeys = []) => {
  if (!fullState || typeof fullState !== "object") return null;

  const priorityCandidates = priorityKeys.map((k) => fullState?.[k]);

  for (const candidate of priorityCandidates) {
    if (candidate?.email) return candidate.email;
    const nested = candidate?.user || candidate?.profile || candidate?.data;
    if (nested?.email) return nested.email;
  }

  for (const key of Object.keys(fullState)) {
    const slice = fullState[key];
    if (!slice || typeof slice !== "object") continue;

    if (typeof slice.email === "string" && slice.email.includes("@")) {
      return slice.email;
    }

    const nested = slice.user || slice.profile || slice.currentDoctor || slice.currentPatient || slice.data;
    if (nested?.email) return nested.email;
  }

  return null;
};

// Clé localStorage utilisée pour savoir si le compte patient lié a déjà
// été créé/mot de passe défini par ce médecin (flow "premier switch").
// ⚠️ Basé sur localStorage => par device/navigateur. Le backend reste la
// vraie source de vérité : si le compte existe déjà (409 "Email already
// in use"), on bascule automatiquement sur le login classique.
const getFirstTimeKey = (doctorId) => `medilink_patientPasswordSet_${doctorId}`;

/**
 * Modal de switch de rôle, utilisé dans les DEUX sens :
 * 1. Patient -> Doctor : prop `patient={user}`
 * 2. Doctor -> Patient : prop `doctor={currentUser}`
 *
 * Pour le sens Doctor -> Patient, la PREMIÈRE fois, on affiche un formulaire
 * "définir un nouveau mot de passe" (+ confirmation) et on crée le compte
 * patient lié via le endpoint de signup patient existant
 * (`ENDPOINTS.USERS.REGISTER` -> POST /users/register), même email que le
 * docteur. Une fois créé, on se connecte directement avec ce mot de passe.
 * Les fois suivantes, on affiche le formulaire de connexion classique.
 */
export default function SwitchModal({ open, onClose, patient, doctor }) {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const direction = doctor ? "toPatient" : "toDoctor";
  const sourceUser = doctor || patient;
  const doctorId = doctor?.id || doctor?._id;

  const isFirstTimeToPatient =
    direction === "toPatient" &&
    !!doctorId &&
    !localStorage.getItem(getFirstTimeKey(doctorId));

  const fullReduxState = useSelector((state) => ({
    auth: state.auth,
    doctor: state.doctor,
    doctors: state.doctors,
    doctorProfile: state.doctorProfile,
    user: state.user,
    patient: state.patient,
    profileDoctor: state.profileDoctor,
    currentDoctor: state.currentDoctor,
    currentPatient: state.currentPatient,
    admin: state.admin,
  }));

  const resolvedEmail = useMemo(() => {
    const fromProp =
      sourceUser?.email ||
      sourceUser?.userEmail ||
      sourceUser?.emailAddress ||
      sourceUser?.user?.email ||
      sourceUser?.account?.email ||
      sourceUser?.profile?.email;
    if (fromProp) return fromProp;

    const priorityKeys =
      direction === "toPatient"
        ? ["doctor", "doctors", "doctorProfile", "currentDoctor"]
        : ["auth", "user", "patient"];

    const fromRedux = findEmailInState(fullReduxState, priorityKeys);
    if (fromRedux) return fromRedux;

    const token =
      direction === "toPatient"
        ? Cookies.get("doctorAccessToken") || Cookies.get("doctorToken") || Cookies.get("doctor_token")
        : Cookies.get("accessToken") || Cookies.get("auth_token") || Cookies.get("token");

    const payload = decodeJwtPayload(token);
    const fromToken = payload?.email || payload?.userEmail;
    if (fromToken) return fromToken;

    return null;
  }, [sourceUser, fullReduxState, direction]);

  const resetState = () => {
    setPassword("");
    setConfirmPassword("");
    setError("");
    setLoading(false);
    setAcceptPrivacy(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const loginAsPatient = async (pwd) => {
    const res = await axiosAuth.post(ENDPOINTS.AUTH.LOGIN, {
      email: resolvedEmail,
      password: pwd,
    });
    const data = res.data;

    if (data?.accessToken) {
      Cookies.set("accessToken", data.accessToken);
      Cookies.set("auth_token", data.accessToken);
      localStorage.setItem("userRole", "patient");
      // sessionStorage is tab-scoped, unlike localStorage/cookies — it's
      // what isDoctorSession() trusts first when a doctor and patient are
      // both logged in (e.g. in separate tabs of the same browser).
      window.sessionStorage.setItem("activeSessionRole", "patient");
      message.success("Connecté en tant que patient");
      handleClose();
      navigate("/profile");
    } else {
      // Cas 2FA : le backend a envoyé un code au lieu d'un accessToken direct.
      // ⚠️ Ce modal ne gère pas encore la saisie du code — à brancher si le
      // 2FA doit s'appliquer aussi lors d'un switch (device pas forcément
      // "trusted" côté compte patient nouvellement créé).
      message.info(data?.message || "Vérification supplémentaire requise");
      handleClose();
    }
  };

  const loginAsDoctor = async () => {
    const res = await axiosDoctorAuth.post("/auth/doctor/login-dr", {
      email: resolvedEmail,
      password,
    });
    const data = res.data;

    if (data?.accessToken) {
      Cookies.set("doctorAccessToken", data.accessToken);
      Cookies.set("doctorToken", data.accessToken);
      localStorage.setItem("userRole", "doctor");
      // sessionStorage is tab-scoped, unlike localStorage/cookies — it's
      // what isDoctorSession() trusts first when a doctor and patient are
      // both logged in (e.g. in separate tabs of the same browser).
      window.sessionStorage.setItem("activeSessionRole", "doctor");
    }

    message.success("Connecté en tant que médecin");
    handleClose();
    navigate("/doctorprofile");
  };

  // Premier switch vers patient : on crée le compte patient lié via le
  // signup existant, puis on se connecte avec le mot de passe choisi.
  const handleSetPatientPassword = async () => {
    if (!password || !confirmPassword) {
      setError("Les deux champs sont requis");
      return;
    }
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    if (!resolvedEmail) {
      setError("Impossible de récupérer l'email du compte lié.");
      return;
    }
    if (!doctor?.phone) {
      setError("Numéro de téléphone du compte médecin introuvable.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [firstName, ...rest] = (doctor?.fullName || "").trim().split(" ");

      await axiosUsers.post(ENDPOINTS.USERS.REGISTER, {
        firstName: firstName || "Patient",
        // ⚠️ FIX: si `fullName` ne contient qu'un seul mot (ex: "douddy"),
        // `rest` est vide et `lastName` partait vide, ce qui déclenchait un
        // 400 "Nom obligatoire" côté backend. On retombe sur `firstName`
        // plutôt que sur une chaîne vide.
        lastName: rest.join(" ") || firstName || "Patient",
        email: resolvedEmail,
        phone: doctor.phone,
        password,
        confirmPassword,
        // Pas de genre/date de naissance dispo depuis le compte médecin :
        // placeholder, la patiente les complètera dans son profil.
        gender: "not_specified",
        birthdate: "not_specified",
        acceptPrivacyPolicy: true,
      });

      localStorage.setItem(getFirstTimeKey(doctorId), "true");

      await loginAsPatient(password);
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? "";

      // Le compte patient existe déjà (email ou téléphone déjà utilisé).
      // ⚠️ Le backend renvoie parfois 500 au lieu de 409 pour ce cas — on
      // détecte donc sur le message plutôt que sur le status code pour
      // rester robuste tant que ça n'est pas corrigé côté serveur.
      if (/already in use/i.test(msg)) {
        localStorage.setItem(getFirstTimeKey(doctorId), "true");

        // ⚠️ FIX: au lieu de juste afficher un message et laisser
        // l'utilisateur bloqué sur ce formulaire, on tente directement la
        // connexion avec le mot de passe qu'il vient de saisir. Le compte
        // existe déjà côté backend (même identité email/téléphone que le
        // médecin) donc il y a de bonnes chances que ce soit exactement le
        // compte auquel il essaie d'accéder.
        try {
          await loginAsPatient(password);
          return;
        } catch (loginErr) {
          const loginStatus = loginErr?.response?.status ?? loginErr?.status;
          const loginMsg =
            loginErr?.response?.data?.message ?? loginErr?.message ?? "";

          if (loginStatus === 404) {
            setError("Aucun compte patient trouvé avec cet email.");
          } else if (/invalid credentials/i.test(loginMsg)) {
            setError(
              "Un compte existe déjà avec un mot de passe différent. Veuillez réessayer."
            );
          } else {
            setError(loginMsg || "Impossible de se connecter à ce compte.");
          }
        }
      } else {
        setError(msg || "Impossible de créer le compte patient");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!password) {
      setError("Le mot de passe est requis");
      return;
    }
    if (!resolvedEmail) {
      setError("Impossible de récupérer l'email du compte lié.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (direction === "toDoctor") {
        await loginAsDoctor();
      } else {
        await loginAsPatient(password);
      }
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      const msg =
        err?.response?.data?.message ?? err?.data?.message ?? err?.message ?? "";

      if (status === 404) {
        setError(
          direction === "toPatient"
            ? "Aucun compte patient trouvé avec cet email. Un compte doit peut-être d'abord être créé."
            : "Aucun compte médecin trouvé avec cet email."
        );
      } else {
        const displayMsg = /invalid credentials/i.test(msg)
          ? "Mot de passe incorrect"
          : msg || "Mot de passe incorrect";
        setError(displayMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isFirstTimeToPatient) {
      if (!password || !confirmPassword) {
        setError("Les deux champs sont requis");
        return;
      }
      if (password !== confirmPassword) {
        setError("Les mots de passe ne correspondent pas");
        return;
      }
      if (!acceptPrivacy) {
        setError("Vous devez accepter la politique de confidentialité");
        return;
      }
      setError("");
      handleSetPatientPassword();
    } else {
      handleLogin();
    }
  };

  // PrivacyPolicyPopup en simple consultation (comportement d'origine,
  // inchangé) — n'a aucun effet sur l'acceptation, juste pour lire le texte.
  const handleOpenPrivacyPopup = () => setShowPrivacyPopup(true);
  const handleClosePrivacyPopup = () => setShowPrivacyPopup(false);

  const isToPatient = direction === "toPatient";

  const title = isFirstTimeToPatient
    ? "Définir votre mot de passe patient"
    : isToPatient
    ? "Basculer en mode Patient"
    : "Basculer en mode Médecin";

  const description = isFirstTimeToPatient
    ? "Première connexion à votre compte patient : choisissez un mot de passe."
    : isToPatient
    ? "Entrez votre mot de passe patient pour continuer."
    : "Entrez votre mot de passe médecin pour continuer.";

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      destroyOnClose
      className="switch-modal"
      wrapClassName="switch-modal-wrap"
      width={400}
    >
      <div className="switch-modal__header">
        <div className={`switch-modal__icon ${isToPatient ? "switch-modal__icon--patient" : "switch-modal__icon--doctor"}`}>
          {isToPatient ? "🧑‍⚕️" : "👨‍⚕️"}
        </div>
        <h3 className="switch-modal__title">{title}</h3>
        <p className="switch-modal__description">{description}</p>
      </div>

      <div className="switch-modal__body">
        <Input.Password
          className="switch-modal__input"
          placeholder={isFirstTimeToPatient ? "Nouveau mot de passe" : "Mot de passe"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={isFirstTimeToPatient ? undefined : handleSubmit}
          autoFocus
        />

        {isFirstTimeToPatient && (
          <>
            <Input.Password
              className="switch-modal__input"
              placeholder="Confirmez le mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onPressEnter={handleSubmit}
              style={{ marginTop: 12 }}
            />

            <div style={{ marginTop: 12 }}>
              <Checkbox
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
              >
                J'accepte la{" "}
                <a onClick={handleOpenPrivacyPopup}>
                  politique de confidentialité
                </a>
              </Checkbox>
            </div>
          </>
        )}

        {error && <div className="switch-modal__error">{error}</div>}

        <div className="switch-modal__actions">
          <Button className="switch-modal__cancel" onClick={handleClose} block>
            Annuler
          </Button>
          <Button
            className="switch-modal__submit"
            type="primary"
            loading={loading}
            disabled={isFirstTimeToPatient && !acceptPrivacy}
            onClick={handleSubmit}
            block
          >
            Continuer
          </Button>
        </div>
      </div>

      {/* Lecture seule — comportement d'origine du composant, inchangé */}
      <PrivacyPolicyPopup open={showPrivacyPopup} onClose={handleClosePrivacyPopup} />
    </Modal>
  );
}